/**
 * ReportUpsellCard — the paid-report offer on the quiz result page.
 *
 * Shown between the top-3 cards and the full ranking. Clicking the CTA
 * creates a checkout server-side and redirects to the Lemon Squeezy hosted
 * page (or straight to the report in DEV_CHECKOUT deployments). The quiz
 * snapshot travels as the encodeAnswersForUrl() blob, so the purchased
 * report is frozen to exactly the answers the buyer saw here.
 *
 * Anchoring: one consultant hour costs €100+; the report is a fraction of
 * that. Price itself lives in the payment provider — the card only quotes
 * the display price from i18n so a price test is a copy change.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { track } from '@/features/analytics';
import { encodeAnswersForUrl } from '@/features/quiz/url-codec';
import type { Answers } from '@/features/quiz';
import { createReportCheckout } from './api';

export interface ReportUpsellCardProps {
  answers: Answers;
  lang: 'en' | 'ru';
}

/**
 * Build-time kill switch: the card renders only when the deployment can
 * actually sell (Lemon Squeezy configured on the API, or DEV_CHECKOUT).
 * Set VITE_REPORT_CHECKOUT=true in the env file once the store is live —
 * until then the result page stays exactly as before.
 */
export const REPORT_CHECKOUT_ENABLED = import.meta.env.VITE_REPORT_CHECKOUT === 'true';

export function ReportUpsellCard({ answers, lang }: ReportUpsellCardProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buy = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    track('report_checkout_clicked', { lang });
    try {
      const res = await createReportCheckout({
        answers: encodeAnswersForUrl(answers),
        lang,
      });
      const target = res.checkout_url ?? res.report_url;
      if (target) {
        window.location.assign(target);
        return; // keep the spinner until navigation
      }
      setError(t('report.upsell.error', { defaultValue: 'Checkout failed — try again.' }));
    } catch {
      setError(t('report.upsell.error', { defaultValue: 'Checkout failed — try again.' }));
    }
    setLoading(false);
  };

  const bullets = [
    t('report.upsell.bullet1', {
      defaultValue: 'Visa pathways that actually fit your passport — with income floors',
    }),
    t('report.upsell.bullet2', {
      defaultValue: 'Tax picture for your situation: rates, special regimes, treaty notes',
    }),
    t('report.upsell.bullet3', {
      defaultValue: 'First-year budget: housing taxes, car costs, the numbers behind the score',
    }),
    t('report.upsell.bullet4', {
      defaultValue: 'A 12-month action plan per country + a permanent link, PDF-ready',
    }),
  ];

  return (
    <section
      data-testid="report-upsell"
      className="rounded-xl border-2 border-terra-600 bg-paper-2 p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-terra-600 text-white">
          <FileText className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-lg italic text-ink-900">
            {t('report.upsell.title', {
              defaultValue: 'Turn this result into your personal relocation report',
            })}
          </h2>
          <p className="mt-1 text-sm text-ink-600">
            {t('report.upsell.subtitle', {
              defaultValue:
                'A deep-dive document on your top-3 countries, built from your exact answers. A relocation consultant charges €100+ for one hour; this is the prepared version of that hour.',
            })}
          </p>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5 pl-12 text-sm text-ink-700">
        {bullets.map((b, i) => (
          <li key={i} className="list-disc">{b}</li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-3 pl-12">
        <Link
          to={`/report/preview?a=${encodeAnswersForUrl(answers)}`}
          onClick={() => track('report_checkout_clicked', { lang, surface: 'upsell_preview' })}
          data-testid="report-preview-link"
          className="inline-flex h-10 items-center rounded-md bg-terra-600 px-4 text-sm font-medium text-paper transition-colors hover:bg-terra-700"
        >
          {t('report.upsell.previewCta', { defaultValue: 'Preview your report — free' })}
        </Link>
        <button
          type="button"
          onClick={buy}
          disabled={loading}
          data-testid="report-buy"
          className="text-sm font-medium text-terra-700 underline underline-offset-2 hover:text-terra-800 disabled:opacity-50"
        >
          {loading
            ? t('report.upsell.loading', { defaultValue: 'Opening checkout…' })
            : t('report.upsell.cta', { defaultValue: 'Get my report — €29' })}
        </button>
      </div>
      <p className="mt-2 pl-12 font-mono text-[11px] text-ink-400">
        {t('report.upsell.note', {
          defaultValue: 'One-time payment · instant access · permanent link',
        })}
      </p>
      {error && (
        <p className="mt-2 pl-12 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
